import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import Table from "./ui/Table";
import Pagination from "./ui/Pagination";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

// Format date to readable format (e.g., "Oct 8, 2025, 9:18 PM")
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  };

  return new Date(dateString).toLocaleString('en-US', options);
};

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [filter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const itemsPerPage = 10;
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filter, currentPage]);


  const fetchUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${baseUrl}/users/list?search=${search}&status=${filter}&page=${currentPage}&perPage=${itemsPerPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const result = await response.json();
      if (result.status === true && Array.isArray(result.data)) {
        setUsers(result.data);
        setTotalUsers(result.pagination?.total || 0);
      } else {
        throw new Error(result?.message || "Failed to fetch user list");
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const filteredData = users.filter((item) => {
    const fullName = `${item.firstName} ${item.lastName}`;
    return (
      (filter === "All" || item.status === filter) &&
      (fullName.toLowerCase().includes(search.toLowerCase()) ||
        item.email.toLowerCase().includes(search.toLowerCase()) ||
        item.username.toLowerCase().includes(search.toLowerCase()))
    );
  });

  const totalPages = Math.ceil(totalUsers / itemsPerPage) || 1;

  const columns = [
    { key: "username", header: "Username" },
    { key: "email", header: "Email" },
    {
      key: "coin",
      header: "Coin",
      render: (user) => <span className="text-brand-light font-medium">{user.coin}</span>,
    },
    { key: "referralCount", header: "Referral Count" },
    {
      key: "createdAt",
      header: "Joining Date",
      render: (user) => <span className="whitespace-nowrap">{formatDate(user.createdAt)}</span>,
    },
  ];

  return (
    <div className="flex flex-col md:flex-row bg-gray-900 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-8">
          <h4 className="text-2xl font-semibold mb-4 text-white">Users</h4>
          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 bg-gray-800 p-4 rounded-lg shadow-md">
            <span className="text-sm text-gray-300">
              Showing {filteredData.length} of {totalUsers} users
            </span>
            <div className="relative w-full sm:w-1/3">
              <input
                className="w-full pl-10 pr-4 py-2 bg-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1); // Reset page to 1 on search
                }}
              />
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>

          {error && <p className="text-red-400 mb-4">Error: {error}</p>}

          <Table
            columns={columns}
            rows={filteredData}
            rowKey={(user) => user.id}
            loading={loading}
            emptyMessage="No users found."
            onRowClick={(user) => navigate(`/user/${user.userId}`)}
          />

          <div className="mt-6">
            <Pagination page={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserList;
